export const DEFAULT_NUMBER_OPTIONS = {
  locale: 'pt-BR',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
};

export const toCurrency = (value, currencyType) => {
  return Number(value).toLocaleString('pt-br', {
    style: 'currency',
    currency: currencyType ?? 'BRL',
    maximumFractionDigits: 2,
  });
};

export const fromCurrencyToNumber = (value) => {
  let amount = value.split(/(\s+)/)[2];
  amount = amount.replace(/\./g, '').replace(/,/g, '.');
  return parseFloat(amount);
};

export const handleSetCurrency = (value, currencyType) => {
  if (typeof value === 'number') {
    return toCurrency(value, currencyType);
  }
  return value;
};

export const getDiscountPrice = (value, discount, type) => {
  let valueDiscount = discount;
  if (discount > 0) {
    if (type === 'percentage') {
      valueDiscount = value * (discount / 100);
    }
  }
  return valueDiscount;
};

const convertNegativeValue = (value, currency, isPercentage) => {
  const divisor = value <= -1.0e9 ? 1000000000 : value <= -1.0e6 ? 1000000 : 1;
  return `${isPercentage ? '' : 'R$ '}${parseFloat(
    value / divisor,
  ).toLocaleString('pt-br', {
    currency,
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  })} ${value <= -1.0e9 ? 'bi' : value <= -1.0e6 ? 'mi' : ''}`;
};

export const convertValue = (
  value,
  valueType = '',
  hasPostFix = true,
  style = 'currency',
  currency = 'BRL',
  maximumFractionDigits = 2,
  minimumFractionDigits = 2,
  isPercentage = false,
) => {
  if (value === null || value === undefined) return '';
  if (value >= 1.0e9) {
    return `${parseFloat(value / 1000000000).toLocaleString('pt-br', {
      style,
      currency,
      maximumFractionDigits,
      minimumFractionDigits,
    })} ${hasPostFix ? 'bi' : ''}`;
  } else if (valueType === 'million' || value >= 1.0e6) {
    return `${parseFloat(value / 1000000).toLocaleString('pt-br', {
      style,
      currency,
      maximumFractionDigits,
      minimumFractionDigits,
    })} ${hasPostFix ? 'mi' : ''}`;
  } else if (value < 0) {
    return convertNegativeValue(value, currency, isPercentage);
  } else {
    return parseFloat(value).toLocaleString('pt-br', {
      style,
      currency,
      maximumFractionDigits,
      minimumFractionDigits,
    });
  }
};

export const formatCompactAxisCurrency = (value) =>
  convertValue(value, '', true, 'currency', 'BRL', 1, 0);

export const convertNumber = (
  value,
  maximumFractionDigits = 3,
  minimumFractionDigits = 0,
) =>
  parseFloat(value).toLocaleString('pt-BR', {
    maximumFractionDigits,
    minimumFractionDigits,
  });

export const calculateRadarValue = (value, divisor = 1000000, digits = 1) => {
  return +(value / divisor).toFixed(digits);
};

export const calculatePercentageValue = (
  sliceValue,
  totalValue,
  digits = 1,
) => {
  return +((sliceValue / totalValue) * 100).toFixed(digits);
};

export const formatRadarValue = (value, digits = 1) => {
  return value.toLocaleString('pt-br', {
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
  });
};

export const formatRadarText = (
  text,
  maximumFractionDigits = 2,
  minimumFractionDigits = 2,
) => {
  const maskNumber = (value) => {
    const parsedValue = value.replace(/\./g, '').replace(',', '.');
    const valueNumber = Number(parsedValue);

    if (valueNumber >= 1000000000) {
      return `${(valueNumber / 1000000000).toLocaleString('pt-br', {
        style: 'currency',
        currency: 'BRL',
        maximumFractionDigits,
        minimumFractionDigits,
      })} bi`;
    }
    if (valueNumber >= 1000000) {
      return `${(valueNumber / 1000000).toLocaleString('pt-br', {
        style: 'currency',
        currency: 'BRL',
        maximumFractionDigits,
        minimumFractionDigits,
      })} mi`;
    }

    return valueNumber;
  };

  return text.replace(/(\d{1,3}\.){1,}\d{3}(,\d+)?/g, maskNumber);
};

export const formatBytes = (bytes, isKB = false) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return (
    parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i + isKB]
  );
};

export const intlFormatNumber = (value, maxDigits = 2) => {
  return new Intl.NumberFormat('pt-BR', {
    maximumFractionDigits: maxDigits,
  }).format(value);
};

export const formatAmountBRL = (value) => {
  if (value === null || value === undefined) return '-';
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return '-';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numeric);
};

export const formatPercentageValue = (value, fractionDigits = 2) => {
  if (value === null || value === undefined) return '-';
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return '-';
  const formatted = new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(numeric);
  return `${formatted}%`;
};

export function formatNumber(
  value,
  locale = 'pt-BR',
  minimumFractionDigits = 0,
  maximumFractionDigits = 1,
  fallbackValue = '-',
) {
  if (value === null || value === undefined) {
    return fallbackValue;
  }

  const formatter = new Intl.NumberFormat(locale, {
    minimumFractionDigits,
    maximumFractionDigits,
  });

  if (Math.abs(value) >= 1_000_000_000) {
    return `${formatter.format(value / 1_000_000_000)}bi`; // Bilhão
  } else if (Math.abs(value) >= 1_000_000) {
    return `${formatter.format(value / 1_000_000)}mi`; // Milhão
  } else if (Math.abs(value) >= 1_000) {
    return `${formatter.format(value / 1_000)}mil`; // Mil
  }
  return formatter.format(value);
}
